import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------

import Disclaimer from '../../others/Disclaimer';
import FareListTwoColumns from '../../lists/FareListTwoColumns';
import FareItemMediumSize from '../../items/FareItemMediumSize';
import LoadMorePaginator from '../../pagination/LoadMorePaginator';
import Loading from '../../others/Loading';
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Alert from '../../alerts/ContainerErrorAlert';
import NoResults from '../../alerts/NoFeaturedPopularFaresAlert';

import Skeleton from '../../others/Skeleton/SkeletonFareListTwoColumnsMobile';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';

//-------------------------------------
// Integration Layer Default Actions
//-------------------------------------
import { getFares } from '../../../_integrations/everymundo/fares';
import { getAirports } from '../../../_integrations/everymundo/airports';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/groupedRoutesAggregation';

import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useListItemFocus, useListFocusHandlers } from '../_commons/list';
import { useFareItemProps } from '../_commons/tracking';
import InlineFilters from '../../controls/InlineFilters/InlineFiltersMobile';
import HeaderContainer from '../../elements/HeaderContainer';
import SecondaryFilters from '../../controls/SecondaryFilters';

function FareListContainerMobile(props) {
  const {
    showLoading,
    showSorting,
    showBottomDisclaimer,
    showFilters,
    list,
    filters,
    fareItem,
    fareItemOnClickAction,
    bottomDisclaimerContentSettings,
    pagination,
    sortingSettings,
    skeletonSettings,
    loading,
    noFaresBehavior,
    notResults,
    alert,
    colClearAll,
    colSorting,
    colStops,
    direction,
    bottomDisclaimer,
    loadMoreContentSettings,
    showSkeletonScreen,
    paginationSettings,
    initialFares,
    faresTrasformationLogic,
    theme,
    prePopulation,
    integrationSettings,
    meta,
    getFares: getFaresFn,
    getAirports: getAirportsFn,
    // deprecated props
    showOrigin, // use filters.availableFilters
    showDestination, // use filters.availableFilters
    showStops, // use filters.availableFilters
    origin,
    destination,
    stopsSettings,
    sorting,
    clearAll,
    filter,
    originDestinationLogic,
    showPagination,
    secondaryFilters,
    showSecondaryFilters = false,
    enableSEO = true
  } = props;

  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);

  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    containerSettings: filter,
    originDestinationSettings: {
      components: {
        origin,
        destination
      },
      showOrigin,
      showDestination
    },
    stopSettings: stopsSettings,
    stopColumnSettings: colStops,
    sortingSettings: { ...sortingSettings, ...sorting },
    sortingColumnSettings: colSorting,
    clearAllSettings: clearAll,
    clearAllColumnSettings: colClearAll,
    ...filters
  };

  const availableFilters = filters.availableFilters
    ? filters.availableFilters
    : [
        (showOrigin || showDestination) && 'OriginDestination',
        showStops && 'Stops',
        showSorting && 'Sorting'
      ].filter(Boolean);

  const { focusIndex, setFocusIndex, ...focusHandlers } = useListFocusHandlers();

  const {
    fares: { match, ...fares },
    dispatch,
    tracking,
    retry,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: { prePopulation, integrationSettings, meta, getAirports: getAirportsFn },
    initialData: initialFares,
    minInitialResults: noFaresBehavior === 'hidden' ? 1 : null,
    getFares: getFaresFn,
    paginationSettings,
    faresTransformationLogic: faresTrasformationLogic,
    availableFilters: showFilters ? availableFilters : [],
    showSecondaryFilters,
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    hideBudgetOnMultiCurrency: filters.hideBudgetOnMultiCurrency,
    showOrigin:
      filtersConfig.originDestinationSettings && filtersConfig.originDestinationSettings.showOrigin,
    showDestination:
      filtersConfig.originDestinationSettings &&
      filtersConfig.originDestinationSettings.showDestination,
    ...focusHandlers
  });

  const itemSettings = useFareItemProps(fareItem, dispatch, fareItemOnClickAction);

  const [nextFocusIndex, listItemRef] = useListItemFocus({
    data: fares.data,
    focusIndex,
    setFocusIndex
  });
  const alertRef = useRef(null);
  const loadMoreRef = useRef(null);

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }
  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }
  const header = (
    <>
      {showSkeletonScreen && match('SKELETON') && (
        <Skeleton {...skeletonSettings} position="absolute" width="100" />
      )}
      <HeaderContainer
        defaultTitleValue={'FIND THE BEST DEALS WITH AMERICAN'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
    </>
  );

  return (
    <InlineFilters
      className={`${FareListContainerMobile.componentName} ${className}`}
      style={myStyle}
      dataTestId="fareListContainer"
      header={header}
      {...filtersConfig}
      {...filtersState}
    >
      <Box position="relative">
        {showLoading && match('LOADING') && <Loading {...loading} />}
        {showSecondaryFilters && (
          <SecondaryFilters
            {...filtersConfig}
            {...secondaryFilters}
            {...filtersState}
            availableFilters={filtersState.availableSecondaryFilters}
            filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
          />
        )}
        {match('ERROR') && (
          <Row>
            <Col floatRight>
              <Alert
                {...alert}
                focusReferences={alertRef}
                tryAgain={retry}
                onCloseAction={() => {}}
              />
            </Col>
          </Row>
        )}
        {match('NO_RESULTS') && (
          <NoResults
            labelKey="cmp-no-results-text"
            buttonLabelKey="cmp-no-results-button"
            buttonRefreshingLabelKey="cmp-no-results-button-refreshing"
            icon="broken-heart"
            showIcon
            showButton
            onClick={retry}
            {...notResults}
            isRefreshing={fares.isLoading}
          />
        )}
        {match('COMPLETE') && (
          <FareListTwoColumns
            focusOn={nextFocusIndex}
            ItemComponentInstance={FareItemMediumSize}
            itemsData={fares.data}
            itemSettings={itemSettings}
            focusReferences={(ref) => {
              listItemRef.current = ref;
            }}
            onEscKeyPress={() => loadMoreRef.current.focus()}
            {...list}
          />
        )}
        {showPagination && fares.hasMore() && (
          <Box textAlign="center" marginTop="2" marginBottom="3" {...loadMoreContentSettings}>
            <LoadMorePaginator
              isLoading={match('LOADING')}
              onClickAction={fares.loadMore}
              focusReferences={loadMoreRef}
              {...pagination}
            />
          </Box>
        )}
        {showBottomDisclaimer && (
          <Box {...bottomDisclaimerContentSettings}>
            <Disclaimer fontSize="3" {...bottomDisclaimer} />
          </Box>
        )}
      </Box>
    </InlineFilters>
  );
}

FareListContainerMobile.propTypes = {
  /**
   * Alert Component
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * BottomDisclaimer Component
   */
  bottomDisclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  bottomDisclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component name
   */
  componentName: PropTypes.string,
  /**
   * To be used to set if the container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Props of clearAll
   */
  clearAll: PropTypes.objectOf(PropTypes.any),
  /**
   * props of clearAll col
   */
  colClearAll: PropTypes.objectOf(PropTypes.any),
  /**
   * props of origin col
   */
  colOrigin: PropTypes.objectOf(PropTypes.any),
  /**
   * props of destination col
   */
  colDestination: PropTypes.objectOf(PropTypes.any),
  /**
   * props of sorting col
   */
  colSorting: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings for the column container of the stops filter
   */
  colStops: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings of destination
   */
  destination: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of container
   */
  direction: PropTypes.string,
  /**
   * FareItem Component
   */
  fareItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * Component Fares
   */
  fares: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible ways to get fares from API
   */
  faresTrasformationLogic: PropTypes.oneOf(['grouped-routes', 'agreggation']),
  /**
   * Settings of filters (deprecated)
   */
  filter: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Function to getFares
   */
  getFares: PropTypes.func,
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * headerLocation
   */
  headerLocation: PropTypes.objectOf(PropTypes.any),
  /**
   * Component Initial Fares
   */
  initialFares: PropTypes.arrayOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if loading fares
   */
  isLoadingFares: PropTypes.bool,
  /**
   * List Component
   */
  list: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: PropTypes.oneOf(['hidden', 'showNoResults']),
  /**
   * NotResults Component
   */
  notResults: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings of origin
   */
  origin: PropTypes.objectOf(PropTypes.any),
  /**
   * Pagination Component
   */
  pagination: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings of the Pagination
   */
  paginationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Origin Component
   */
  pageContext: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Id of the settings
   */
  settingsId: PropTypes.string,
  /**
   * Shows Alert Component
   */
  showAlert: PropTypes.bool,
  /**
   * Shows BottomDisclaimer Component
   */
  showBottomDisclaimer: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Shows Pagination Component
   */
  showPagination: PropTypes.bool,
  /**
   * Shows Filters
   */
  showFilters: PropTypes.bool,
  /**
   * Shows stops filter
   */
  showStops: PropTypes.bool,
  /**
   * Shows Origin
   */
  showOrigin: PropTypes.bool,
  /**
   * Shows Destination
   */
  showDestination: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be true)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Show the Sorting Component
   */
  showSorting: PropTypes.bool,
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * Show the Title Component
   */
  showTitle: PropTypes.bool,
  /**
   * Show the TopDisclaimer Component
   */
  showTopDisclaimer: PropTypes.bool,
  /**
   * Sorting Component
   */
  sorting: PropTypes.objectOf(PropTypes.any),
  /**
   * Sorting Settings Component
   */
  sortingSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Stops filter Settings
   */
  stopsSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  titleKey: PropTypes.string,
  /**
   * Title component settings
   */
  titleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * TopDisclaimer Component
   */
  topDisclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  topDisclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * loadMoreContentSettings props
   */
  loadMoreContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * headerContentSettings props
   */
  headerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Shows Secondary Filters
   */
  showSecondaryFilters: PropTypes.bool,
  /**
   *Secondary Filters Settings
   */
  secondaryFilters: PropTypes.objectOf(PropTypes.any),
  /**
   * All meta configurations
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ])
};

FareListContainerMobile.defaultProps = {
  alert: {
    rounded: '@theme:card.rounded'
  },
  bottomDisclaimer: {},
  bottomDisclaimerContentSettings: {},
  componentName: 'FareListContainer',
  containerType: 'stateful',
  clearAll: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    borderNone: true,
    icon: {
      iconColor: 'light-18'
    }
  },
  colClearAll: {},
  colOrigin: {},
  colDestination: {},
  colSorting: {},
  colStops: {},
  destination: {},
  direction: 'ltr',
  fareItem: {},
  fareItemInteractionType: 'popup',
  fareItemOnClickAction: null,
  fares: null,
  faresTrasformationLogic: 'grouped-routes',
  filter: {},
  filters: {},
  secondaryFilters: {},
  getFares,
  getAirports,
  headerLocation: {},
  initialFares: null,
  integrationSettings: {},
  isLoadingFares: true,
  list: {},
  loading: {},
  noFaresBehavior: 'hidden',
  notResults: {},
  origin: {},
  pagination: {},
  paginationSettings: {
    type: 'load_more',
    step: 10,
    limit: 30,
    intialVisibleItems: 10
  },
  // _SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED :)
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: prePopulationDefaultProps,
  settingsId: 'SomethingID',
  showAlert: true,
  showBottomDisclaimer: false,
  showLoading: true,
  showPagination: true,
  showSkeletonScreen: true,
  showSorting: true,
  showSubTitle: false,
  showTitle: true,
  showTopDisclaimer: true,
  showFilters: true,
  showOrigin: false,
  showDestination: false,
  showStops: false,
  stopsSettings: {},
  sorting: {},
  sortingSettings: {
    options: ['price_low_to_hight', 'price_hight_to_low', 'departure_date', 'no_sorting']
  },
  subTitleKey: 'cmp-fare-list-two-columns-sub-title',
  subTitleSettings: {},
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  titleKey: 'cmp-fare-list-two-columns-title',
  titleSettings: {},
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareListTwoColumn'
  },
  topDisclaimer: {},
  topDisclaimerContentSettings: {},
  loadMoreContentSettings: {},
  headerContentSettings: {
    gutters: true
  },
  subHeaderContentSettings: {
    gutters: true
  },
  translate: () => {},
  localizationData: null,
  theme: null,
  meta: null,
  originDestinationLogic: OriginDestinationLogic.SAME
};

FareListContainerMobile.componentName = 'FareListContainerMobile';

// HOC with common functionality to all containers
export default withCommonEMContainer(FareListContainerMobile);
